<template>
  <!-- XOL-67 (V3, v2.1; decisions 153, 157, 158 Q3, 159; UX spec 2.1a, M-36): Step 1, Requested. The request as an INLINE form: no Edit button, no popup.
       Top to bottom: the facts (read-only), the lock note when the viewer cannot edit, Site Name / Site ID, then RtaRequestFields (the locality, the requirements,
       and the requested point and its map LAST). Each field saves on its own, after a pause, as a versioned FieldPatch (`PUT /actions/{id}`, one leaf path per field,
       the point's latitude + longitude in ONE body, each path with its own base version), on the C-12 card. The gate is rtaStage.canEditRequestField
       (computed over the store), one rule for every field, Site Name and Site ID included (decision 173): the client and a Tower Co responder until the first
       proposal, then a Tower Co manager only. The server decides again, and its refusal (request_locked, a 403, stale_field, 422) is worded here. -->
  <div class="rta-request" :class="{ 'is-stacked': isMediumOrLess }" :data-lock="lockReason">

    <!-- One lock note, not one per field (U-20); every bold value points at it. -->
    <el-alert
      v-if="lockNote"
      :id="noteId"
      class="rta-request__lock"
      type="info"
      show-icon
      :closable="false"
      :title="lockNote" />

    <!-- Decision 186 (Carlos, 2026-10-29): Step 1 looks like the create form. Two titled sections, Details (the facts, Site Name and its hint, Site ID, the
         Locality, Lat / Lng) and Structure Details (Station Type and the requirements), then "Find a place" and the map. The facts and the names go in
         RtaRequestFields' `lead` slot, so everything sits in its one form (no nested forms). -->
    <rta-request-fields
      mode="step"
      sections
      :value="form"
      :editable="editable"
      :errors="errors"
      :structure-label="structureLabel"
      :stacked="isMediumOrLess"
      :pin-markers="pins"
      @input="onInput"
      @change="onChange"
      @open-option="onOpenOption">

      <template #lead>
        <el-form-item v-if="showClient" label="Client" data-fact="client"><b>{{ clientName }}</b></el-form-item>
        <el-form-item label="Reference" data-fact="reference"><b>{{ reference }}</b></el-form-item>
        <el-form-item v-if="requestedBy" label="Requested" data-fact="requested"><b>{{ requestedBy }}</b></el-form-item>

        <text-input
          v-if="canEditName"
          key="name-input"
          class="rta-request__name"
          data-field="siteName"
          label="Site Name"
          aria-label="Site Name"
          :maxlength="120"
          :value="form.siteName"
          :error="errors.siteName || null"
          :error-id="errors.siteName ? 'rta-request-name-error' : null"
          :described-by="nameHint ? hintId : null"
          :validate-event="false"
          @change="onName" />
        <el-form-item v-else key="name-fact" label="Site Name" data-field="siteName">
          <b :aria-describedby="boldDescribedBy('siteName')">{{ form.siteName || 'Not set' }}</b>
        </el-form-item>
        <small v-if="nameHint" key="name-hint" :id="hintId" class="text-muted rta-note rta-request__name-hint" :class="{ 'has-error': !!errors.siteName }" data-note="source">{{ nameHint }}</small>

        <text-input
          v-if="canEditId"
          key="id-input"
          class="rta-request__id"
          data-field="siteId"
          label="Site ID"
          aria-label="Site ID"
          :maxlength="16"
          :value="form.siteId"
          :error="errors.siteId || null"
          :error-id="errors.siteId ? 'rta-request-id-error' : null"
          :validate-event="false"
          @change="onId" />
        <el-form-item v-else key="id-fact" label="Site ID" data-field="siteId">
          <b :aria-describedby="boldDescribedBy('siteId')">{{ form.siteId || 'Not set' }}</b>
        </el-form-item>
      </template>

    </rta-request-fields>

  </div>
</template>


<script>

/*
 * XOL-67 (V3): Step 1 of the RTA drawer. See the template header for the shape. What it does, in order:
 *
 *   an edit (`change` of RtaRequestFields, or Site Name / Site ID)   it is PENDING (the form shows it, the server does not hold it yet) and
 *                                                                    rtaAutosave restarts its pause
 *   the pause ends                                                   rtaNominal.nominalChanges (pending vs what the server last held) ->
 *                                                                    rtaFieldSave.requestBodies (one body per field, the point's two paths in ONE) ->
 *                                                                    PUT /actions/{id} with X-Edit-Session, one body after the other
 *   a field that fails the form's own check (or a lone coordinate)   is HELD: not sent, its message shown (rtaNominal.nominalFieldProblem /
 *                                                                    pointProblem, hand-off 1); the other fields still go
 *   the answer (the action)                                          the sent paths' bases move (rtaFieldSave.savedBase), what the user has not
 *                                                                    edited since takes the server's value, the drawer follows (`rta-changed`)
 *   a refusal                                                        rtaRefusal.saveRefusal reads the locks first: 'locked' / 'closed' word it,
 *                                                                    drop the edits and show what the server holds (the form turns read-only);
 *                                                                    'stale' is the XOL-596 card, the conflicting fields take the server's value;
 *                                                                    'invalid' puts the plain-copy message on its field
 *
 * The RTA's own refusal handling, NOT RequestMixins' field-save path: that one treats every 409 but request_closed as stale and every 422 as a bug
 * (V1 hand-off), and request_locked / site_name_taken are neither.
 *
 * `flush()`, `hasPending()` and `isRefused()` are the drawer's: it saves what waits before a command or a step change, and before it closes.
 */

import axios from '@/lib/axiosClient'
import RtaCommandMixins from '@/mixins/RtaCommandMixins'
import DateMixins from '@/mixins/DateMixins'

import * as rtaStage from '@/lib/rtaStage'
import * as rtaOption from '@/lib/rtaOption'
import * as rtaNominal from '@/lib/rtaNominal'
import * as rtaFieldSave from '@/lib/rtaFieldSave'
import { saveRefusal, staleConflicts, parseFailure } from '@/lib/rtaRefusal'
import { createAutosave } from '@/lib/rtaAutosave'
import { keepalivePut } from '@/lib/keepalive'

import RtaRequestFields from '@/components/requests/acquire/RtaRequestFields'
import TextInput from '@/components/atoms/inputs/TextInput'

function has (o, k) {
  return Object.prototype.hasOwnProperty.call(o, k)
}

function nominalOf (action) {
  var n = action && action.state && action.state.nominal

  return n && typeof n === 'object' ? n : {}
}

// The form's values from a request: every field of state.nominal, a missing one null.
function valuesOf (nominal) {
  var out = {}

  rtaNominal.NOMINAL_FIELDS.forEach(function (f) {
    out[f] = nominal[f] === undefined ? null : nominal[f]
  })

  return out
}

// Walk the structure-type tree of the config ({ label, value, children }) for a leaf's label.
function findLabel (list, value) {
  var found = ''

  ;(list || []).forEach(function (item) {
    if (found) {
      return
    }

    if (item && String(item.value) === String(value)) {
      found = item.label || ''
    } else if (item && item.children) {
      found = findLabel(item.children, value)
    }
  })

  return found
}

export default {
  name: 'rta-request',

  mixins: [RtaCommandMixins, DateMixins],

  // The drawer's form scope: its edit session id merges a burst of saves into one Activity row (API DEV-17, P-S27 "Send X-Edit-Session").
  inject: {
    requestSyncScope: {
      default: null
    }
  },

  components: {
    RtaRequestFields,
    TextInput
  },

  props: {

    // The RTA as the drawer holds it (GET /actions/{id}): the request is `state.nominal`.
    rta: {
      type: Object,
      required: true
    },

    // The RTA's option rows, for the pins on the map (read-only; a click opens the option).
    options: {
      type: Array,
      default: function () { return [] }
    },

    // The drawer's own contentVersion: the action's, kept up to date by the drawer (it does not copy the versions into the action).
    contentVersion: {
      type: Number,
      default: null
    },

    // A record seen from elsewhere (a site drawer) or a read-only drawer: nothing is editable.
    readOnly: {
      type: Boolean,
      default: false
    }

  },


  data () {
    return {
      // The form's working copy of the request, wire names. It is the user's to change; a re-read of the RTA moves the fields the user has not edited.
      form: valuesOf(nominalOf(this.rta)),

      // Messages on fields, { field: message }: the server's 422 in plain copy, and the fields held back (see sendRequest).
      errors: {}
    }
  },


  computed: {

    // The store, as computed (not data()): it hydrates after the first paint.
    viewerOrg () {
      return this.org()
    },

    viewerProfile () {
      return this.profile()
    },

    nominal () {
      return nominalOf(this.rta)
    },

    towerCo () {
      return this.towerCoName()
    },

    client () {
      return this.rta.client || this.rta.organisation || null
    },

    clientName () {
      var c = this.client

      return c ? (c.name || c.shortName || '') : ''
    },

    // The Tower Co sees whose request it is; the client knows (UX spec 2.1a item 1).
    showClient () {
      return this.isTowerCo() && !!this.clientName
    },

    reference () {
      return rtaStage.referenceOf(this.rta)
    },

    requestedBy () {
      var at = this.rta.created_at

      if (!at) {
        return ''
      }

      return (this.rta.created_by ? 'by ' + this.rta.created_by + ' on ' : 'on ') + this.formatDateTime(at)
    },

    // May this viewer edit this field? The gate is rtaStage.canEditRequestField; a read-only drawer edits nothing.
    editable () {
      var self = this
      var out = {}

      rtaNominal.NOMINAL_FIELDS.forEach(function (f) {
        out[f] = !self.readOnly && rtaStage.canEditRequestField(f, self.rta, self.viewerOrg, self.viewerProfile)
      })

      return out
    },

    // Why the request is not editable ('' when it is): rtaStage.requestLockReason.
    lockReason () {
      return this.readOnly ? 'viewonly' : rtaStage.requestLockReason(this.rta, this.viewerOrg, this.viewerProfile)
    },

    canEditName () {
      return !!this.editable.siteName
    },

    canEditId () {
      return !!this.editable.siteId
    },

    // The one lock note (UX spec 2.1a item 2); a view-only user gets none (nothing in the drawer is editable).
    lockNote () {
      var tc = this.towerCo

      switch (this.lockReason) {
        case 'proposed':
          return 'Only a ' + tc + ' manager can change the request after options have been proposed.'
        case 'finalize':
          return 'The request is frozen while ' + tc + ' finalizes the site. Site Name and Site ID are confirmed in Step 5.'
        case 'closed':
          return 'This RTA is closed. The request can\'t be changed.'
        case 'notallowed':
          return 'Your account can\'t change the request.'
      }

      return ''
    },

    noteId () {
      return 'rta-request-lock-' + this._uid
    },

    // The id of the Site Name hint (UX spec 9). (The note under the names is gone: the names follow the one rule of the request, decision 173.)
    hintId () {
      return 'rta-request-hint-' + this._uid
    },

    // Where the generated name came from; none once a manager has typed one (U-21). The request has a point NOW: the form's.
    nameHint () {
      return rtaNominal.nameSourceHint(this.nominal.siteNameSource, rtaOption.hasPoint(this.form.latitude, this.form.longitude))
    },

    structureLabel () {
      var id = this.form.structure_id

      return id ? findLabel(this.getConfig().structures, id) || String(id) : ''
    },

    // The RTA with the form's point on it: the crosshairs follow what Lat / Lng say (a typed value moves them), not only what the server holds.
    liveRta () {
      var nominal = Object.assign({}, this.nominal, { latitude: this.form.latitude, longitude: this.form.longitude })
      var state = Object.assign({}, this.rta.state, { nominal: nominal })

      return Object.assign({}, this.rta, { state: state })
    },

    // The Step 1 map's pins: the crosshairs (draggable for who may edit the request) and every option's pin, read-only (delta 2.5).
    pins () {
      return rtaOption.markersForRequestMap(this.liveRta, this.options, this.readOnly ? null : { org: this.viewerOrg, profile: this.viewerProfile })
    }

  },


  watch: {

    // The drawer adopted another reading of the RTA (a command, an option layer, someone else's edit): the fields the user has not edited take it.
    nominal () {
      this.adoptNominal()
    },

    // The drawer's version moved (not by a save of this form, which has already taken its own answer): never go back.
    contentVersion (v) {
      if (typeof v === 'number' && (typeof this.version !== 'number' || v > this.version)) {
        this.version = v
      }
    }

  },


  methods: {

    /* ---------------------------------------------------------------------
       Editing
    --------------------------------------------------------------------- */

    // RtaRequestFields' v-model: the new request, whole.
    onInput (value) {
      this.form = Object.assign({}, this.form, value)
    },

    // RtaRequestFields' `change`: only the keys this edit changed (the map's drop: latitude + longitude together).
    onChange (changes) {
      this.edited(changes)
    },

    // A Site Name cannot be cleared or be shorter than 5 characters: said at once, not after the pause (UX spec 6). The edit is held back (sendRequest);
    // the others go. The Site ID is the same (4 to 16).
    onName (value) {
      this.edited({ siteName: value })
      this.checked('siteName', value)
    },

    onId (value) {
      this.edited({ siteId: value })
      this.checked('siteId', value)
    },

    checked (field, value) {
      var m = rtaNominal.nominalFieldProblem(field, value)
      var one = {}

      if (m) {
        one[field] = m
        this.errors = Object.assign({}, this.errors, one)
      }
    },

    // An edit by the user: the form holds it, it is pending, and the pause restarts. The message on a field goes when the field is edited.
    edited (changes) {
      var self = this
      var errors = Object.assign({}, this.errors)
      var form = Object.assign({}, this.form, changes)

      Object.keys(changes).forEach(function (k) {
        self.pending[k] = changes[k]
        delete errors[k]

        // The point's two coordinates answer to one message.
        if (k === 'latitude' || k === 'longitude') {
          delete errors.latitude
          delete errors.longitude
        }
      })

      this.form = form
      this.errors = errors
      this.autosave.changed(Object.assign({}, this.pending))
    },

    // What a bold (read-only) Site Name / Site ID is described by: the lock note when there is one and, for the Site Name, the hint on where its name came
    // from. null when there is none.
    boldDescribedBy (field) {
      var ids = []

      if (field === 'siteName' && this.nameHint) {
        ids.push(this.hintId)
      }

      if (this.lockNote) {
        ids.push(this.noteId)
      }

      return ids.length ? ids.join(' ') : null
    },

    onOpenOption (id) {
      this.$emit('open-option', id)
    },


    /* ---------------------------------------------------------------------
       The versioned save: PUT /actions/{id} (P-S27)
    --------------------------------------------------------------------- */

    url () {
      return '/actions/' + this.rta.id
    },

    // X-Edit-Session (API DEV-17): the drawer's form scope has one id per open drawer, so the saves of a session merge into one Activity row.
    editHeaders () {
      var scope = this.requestSyncScope
      var session = scope && scope.editSession ? scope.editSession : null

      return session ? { 'X-Edit-Session': session } : null
    },

    // The version the next body carries: the latest answer's, else the drawer's.
    currentVersion () {
      return typeof this.version === 'number' ? this.version : this.contentVersion
    },

    // What waits for the server: the pending values that differ from what it held, split into what can go now and what is held back (a value that fails
    // the form's own check, a lone coordinate: hand-off 1) with the message for each held field.
    plan (values) {
      var changes = rtaNominal.nominalChanges(this.stored, values)
      var merged = Object.assign({}, this.stored, values)
      var held = {}

      changes.forEach(function (c) {
        var m = rtaNominal.nominalFieldProblem(c.field, c.value)

        if (m) {
          held[c.field] = m
        }
      })

      // One coordinate alone gets the pair rule's 422, and a lone clear would store half a point: the pair is judged on the merged point.
      if (changes.some(function (c) { return c.field === 'latitude' || c.field === 'longitude' })) {
        var pair = rtaNominal.pointProblem(rtaNominal.coordinate(merged.latitude), rtaNominal.coordinate(merged.longitude))

        if (pair) {
          held.latitude = pair
          held.longitude = pair
        }
      }

      return {
        send: changes.filter(function (c) { return !has(held, c.field) }),
        held: held
      }
    },

    // The auto-save's `send`: resolves with the last answer; REJECTS with the result of the refused command (rtaAutosave reports it once).
    sendRequest (values) {
      var self = this

      // An edit that put the field back to what the server holds is no edit (a held value that was typed over with the stored one).
      Object.keys(this.pending).forEach(function (k) {
        if (self.sameField(k, self.stored[k], self.pending[k])) {
          delete self.pending[k]
        }
      })

      var plan = this.plan(values)
      var heldKeys = Object.keys(plan.held)

      if (heldKeys.length) {
        this.errors = Object.assign({}, this.errors, plan.held)
      }

      if (!plan.send.length) {
        return Promise.resolve({ held: heldKeys.length > 0 })
      }

      var last = null

      // One body after the other, each built just before it goes: the one before it has moved the version and the bases of what it saved. A refusal
      // stops the rest (they stay pending).
      function next (remaining) {
        if (!remaining.length) {
          return Promise.resolve(last)
        }

        var one = rtaFieldSave.requestBodies(self.currentVersion(), remaining, self.bases)[0]
        var rest = remaining.filter(function (c) { return one.paths.indexOf(c.path) === -1 })

        if (!one.body) {
          return Promise.reject(self.noVersion())
        }

        return self.rtaCommand('put', self.url(), one.body, 'RTA', self.editHeaders()).then(function (result) {
          if (!result.ok) {
            return Promise.reject(result)
          }

          last = result.data
          self.saved(result.data, one, plan.send)

          return next(rest)
        })
      }

      return next(plan.send)
    },

    // A body that cannot be built has no version to send: the same page-is-out-of-date answer a 428 gets.
    noVersion () {
      var err = { response: { status: 428, data: { code: 'version_required' } } }

      return { ok: false, err: err, failure: parseFailure(err) }
    },

    // The server took one body. `one` is { paths, body }, `sent` every change of this cycle ([{ field, path, value }]).
    saved (data, one, sent) {
      var action = data && data.action && typeof data.action === 'object' ? data.action : null

      // The drawer follows the answer even when this form has gone (a save that was still waiting when the step changed).
      this.rtaAnnounce(data, this.rta.id)

      if (!action || this._isDestroyed) {
        return
      }

      if (typeof action.contentVersion === 'number') {
        this.version = action.contentVersion
      }

      var self = this
      var movedOn = []

      sent.forEach(function (c) {
        if (one.paths.indexOf(c.path) === -1) {
          return
        }

        var base = rtaFieldSave.savedBase(self.bases, c.path, c.value, action)

        if (typeof base.version === 'number') {
          self.bases[c.path] = base.version
        }

        movedOn.push(c.path)

        // Not edited again since it was sent: it is no longer pending. (A newer value of the same field stays pending and goes next.)
        if (has(self.pending, c.field) && self.sameField(c.field, self.pending[c.field], c.value)) {
          delete self.pending[c.field]
        }
      })

      this.stored = Object.assign({}, nominalOf(action))
      this.adopt(action, movedOn)
    },

    sameField (field, a, b) {
      var one = {}
      var two = {}

      one[field] = a
      two[field] = b

      return Object.keys(rtaNominal.nominalDiff(one, two)).length === 0
    },

    /* ---------------------------------------------------------------------
       Taking what the server holds
    --------------------------------------------------------------------- */

    // The server's request from an action just received: a field the user has not edited since shows the server's value, with that response's version as its
    // base (G-BASE: the version of the response the displayed value came from). `skip` is the paths whose base a save has just set (confirmSaved's rule).
    adopt (action, skip) {
      var self = this
      var n = nominalOf(action)
      var v = typeof action.contentVersion === 'number' ? action.contentVersion : null
      var form = Object.assign({}, this.form)
      var siblings = skip || []

      rtaNominal.NOMINAL_FIELDS.forEach(function (f) {
        var path = rtaNominal.nominalPath(f)

        if (has(self.pending, f) || siblings.indexOf(path) !== -1) {
          return
        }

        form[f] = n[f] === undefined ? null : n[f]

        if (v !== null) {
          self.bases[path] = v
        }
      })

      this.form = form
    },

    // The drawer adopted another reading of the RTA (not this form's own answer): what the user has not edited takes it, and the base of a field whose
    // value moved is the drawer's version.
    adoptNominal () {
      var self = this
      var n = this.nominal
      var form = Object.assign({}, this.form)

      rtaNominal.NOMINAL_FIELDS.forEach(function (f) {
        if (has(self.pending, f)) {
          return
        }

        var server = n[f] === undefined ? null : n[f]

        if (!self.sameField(f, form[f], server)) {
          form[f] = server

          if (typeof self.contentVersion === 'number') {
            self.bases[rtaNominal.nominalPath(f)] = self.contentVersion
          }
        }
      })

      this.form = form
      this.stored = Object.assign({}, n)
    },

    // Read the RTA again (after a lock or a conflict): every pending edit is dropped, the form shows what the server holds, and the drawer follows.
    reread () {
      var self = this

      return axios.get(this.getEndpoint(this.url())).then(function (response) {
        var data = response.data && response.data.data ? response.data.data : response.data

        if (data && typeof data === 'object' && !self._isDestroyed) {
          self.dropEdits()
          self.rtaAnnounce({ action: data }, self.rta.id)
          self.take(data)
        }

        return data || null
      }).catch(function (e) {
        console.error('XOL-67: re-read of RTA', self.rta && self.rta.id, 'failed', e)

        return null
      })
    },

    // An action in hand that replaces what the form holds: version, bases, stored request and values.
    take (action) {
      if (typeof action.contentVersion === 'number') {
        this.version = action.contentVersion
      }

      this.stored = Object.assign({}, nominalOf(action))
      this.adopt(action, [])
    },

    dropEdits () {
      this.pending = {}
      this.errors = {}
      this.autosave.cancel()
    },


    /* ---------------------------------------------------------------------
       Refusals
    --------------------------------------------------------------------- */

    refusalContext () {
      return {
        telco: this.clientName || 'the client',
        towerCo: this.towerCo,
        role: this.isTowerCo() ? 'tcc' : 'client',
        command: 'request',
        userName: (this.viewerProfile && this.viewerProfile.name) || null
      }
    },

    // A refused save, reported once by rtaAutosave. Locks first (rtaRefusal.saveRefusal): a body that is both a lock and a conflict is a lock.
    refusedSave (result) {
      if (!result || typeof result !== 'object' || !('ok' in result) || this._isDestroyed) {
        return
      }

      var err = result.err
      var kind = result.viewOnly || result.unanswered ? 'other' : saveRefusal(err)
      var failure = result.failure || {}
      var data = failure.data || null
      var action = data && data.action && typeof data.action === 'object' ? data.action : null
      var notice = null

      // A 422 that names fields is said ON them, in plain copy; the API's own sentence is not shown as well.
      if (kind === 'invalid' && this.showFieldErrors(data)) {
        return
      }

      notice = this.rtaRefuse(result, this.refusalContext())

      if (kind === 'stale') {
        this.takeConflicts(data, action)
      } else if (kind === 'locked' || kind === 'closed') {
        // Someone proposed (or finalized, or closed it) while the viewer was typing: the edits are gone, the form shows what the server holds and, with the
        // drawer's gates re-evaluated, turns read-only.
        if (action) {
          this.dropEdits()
          this.rtaAnnounce({ action: action }, this.rta.id)
          this.take(action)
        } else {
          this.reread()
        }
      } else if (notice && notice.reread) {
        this.reread()
      }
    },

    // A 422: the plain-copy message on the field it names. True when it was all said on fields; false when a key no field shows is left (a toast says it).
    showFieldErrors (data) {
      var mapped = rtaNominal.nominalFieldErrors(data)

      if (Object.keys(mapped.fields).length) {
        this.errors = Object.assign({}, this.errors, mapped.fields)
      }

      return Object.keys(mapped.fields).length > 0 && mapped.rest.length === 0
    },

    // A 409 stale_field: the fields someone else changed take THEIR value (the card says so), with the answer's version as their base. The user's
    // other edits stay pending and go next.
    takeConflicts (data, action) {
      var self = this
      var conflicts = staleConflicts(data)

      conflicts.forEach(function (c) {
        if (c.field) {
          delete self.pending[c.field]
        }
      })

      // Nothing else waits: the conflict WAS the refusal, and it is said and taken. The form is not "refused" any more (it would stay so, and every command
      // that flushes it first, Reject RTA… among them, would stop on it). A flush that hit the conflict still answers "not saved": its caller stays here.
      if (Object.keys(this.pending).length === 0) {
        this.autosave.cancel()
      } else {
        // What the user edited besides goes on after the refusal has been said, once the usual pause is over.
        setTimeout(function () {
          if (!self._isDestroyed && Object.keys(self.pending).length) {
            self.autosave.changed(Object.assign({}, self.pending))
          }
        }, 0)
      }

      if (action) {
        this.rtaAnnounce({ action: action }, this.rta.id)

        if (typeof action.contentVersion === 'number') {
          this.version = action.contentVersion
        }

        this.stored = Object.assign({}, nominalOf(action))
        this.adopt(action, [])
      } else {
        this.reread()
      }
    },

    /* ---------------------------------------------------------------------
       The drawer's handles
    --------------------------------------------------------------------- */

    // Save what waits, now. Resolves { ok }: false when the save was refused (its notice is up and the close or the command must not go on).
    flush () {
      return this.autosave.flush().then(function (r) {
        return { ok: !!r.ok }
      })
    },

    hasPending () {
      return !!this.autosaveState && (this.autosaveState.pending || this.autosaveState.saving)
    },

    // The last save was refused and nothing newer waits: the screen holds values the server does not.
    isRefused () {
      return this.autosave.refused()
    },

    // The page is being left: what waits goes out as a fetch keepalive request, with the auth headers (DEV-18).
    keepaliveRequest (values) {
      var self = this
      var plan = this.plan(values)
      var bodies = rtaFieldSave.requestBodies(this.currentVersion(), plan.send, this.bases).filter(function (b) { return !!b.body })

      return Promise.all(bodies.map(function (b) {
        return keepalivePut(self.getEndpoint(self.url()), b.body, self.editHeaders())
      }))
    }

  },


  created () {
    var self = this

    // Not reactive: the edits the server does not hold yet ({ field: value }), the request as the server last held it, the base version of each path and
    // the version the next body carries.
    this.pending = {}
    this.stored = Object.assign({}, this.nominal)
    this.version = this.contentVersion
    this.bases = rtaFieldSave.seedBases(rtaNominal.NOMINAL_FIELDS.map(rtaNominal.nominalPath), this.contentVersion)

    this.autosave = createAutosave({
      send: function (values) { return self.sendRequest(values) },
      onRefused: function (result) { self.refusedSave(result) },
      keepalive: function (values) { return self.keepaliveRequest(values) }
    })
    this.autosaveState = this.autosave.state
  },


  beforeDestroy () {
    var save = this.autosave

    // The safety flush: whatever waits is saved now, and the card stops hearing from this form.
    save.flush().then(function () {
      save.destroy()
    })
  }

}
</script>


<style scoped>
  /* Decision 186: the facts and names sit in Details (RtaRequestFields' `lead` slot); the column rules there give every field its column's width. */
  .rta-request__lock {margin: 8px 0 12px;}

  /* Read-only bold facts: Element's 22px form-item gap is for inputs (the drawer's other read-outs do the same). */
  [data-fact] {margin-bottom: 4px;}

  /* The Site ID's message is drawn below the item: leave room so it does not touch the next row. */
  .rta-request:not(.is-stacked) .rta-request__id.is-error {margin-bottom: 40px;}
  .rta-note {display: block; line-height: 1.45; margin: 0 0 8px;}
  .rta-request__name-hint {margin-top: -14px; font-size: 12px;}
  .rta-request__name-hint.has-error {margin-top: 0;}
</style>
